import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { API_BASE_URL } from '../api';
import PublicBrandHeader from '../components/public/PublicBrandHeader';
import PublicBrandShell from '../components/public/PublicBrandShell';

function money(v, currency) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'GBP' }).format(n);
  } catch {
    return `${n.toFixed(2)}`;
  }
}

function statusColor(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'completed' || s === 'accepted' || s === 'paid') return 'success';
  if (s === 'pending' || s === 'sent' || s === 'draft') return 'warning';
  if (s === 'rejected' || s === 'cancelled') return 'error';
  return 'default';
}

export default function PublicClientPortal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}public/client-portal/${token}/dashboard`, {
        headers: { Accept: 'application/json' },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || `HTTP ${res.status}`);
      setData(json);
    } catch (e) {
      setError(e.message || 'Could not load your portal');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const brand = data?.brand;

  useEffect(() => {
    const title = data?.client?.name
      ? `${data.client.name} — Client portal`
      : brand?.company_name
        ? `${brand.company_name} — Client portal`
        : 'Client portal';
    document.title = title;
    return () => {
      document.title = 'WorkWiseSoft';
    };
  }, [data?.client?.name, brand?.company_name]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 520, mx: 'auto', p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const client = data?.client || {};
  const jobs = Array.isArray(data?.jobs) ? data.jobs : [];
  const estimates = Array.isArray(data?.estimates) ? data.estimates : [];
  const invoices = Array.isArray(data?.invoices) ? data.invoices : [];

  return (
    <PublicBrandShell embeddedBrand={brand}>
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <PublicBrandHeader subtitle="Client portal — jobs, estimates and invoices" />
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
        Welcome{client.name ? `, ${client.name}` : ''}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Your jobs, estimates and invoices in one place.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Jobs (${jobs.length})`} />
        <Tab label={`Estimates (${estimates.length})`} />
        <Tab label={`Invoices (${invoices.length})`} />
      </Tabs>

      {tab === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {jobs.length === 0 ? (
            <Typography color="text.secondary">No jobs yet.</Typography>
          ) : (
            jobs.map((j) => (
              <Card key={j.id} variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 800 }}>{j.name || `Job #${j.id}`}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {[j.scheduled_date, j.scheduled_time].filter(Boolean).join(' @ ') || '—'}
                      </Typography>
                      {j.address ? (
                        <Typography variant="body2" color="text.secondary">
                          {j.address}
                        </Typography>
                      ) : null}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Chip size="small" label={j.status || '—'} color={statusColor(j.status)} sx={{ mb: 0.5 }} />
                      <Typography sx={{ fontWeight: 700 }}>{money(j.price, j.currency)}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      ) : null}

      {tab === 1 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {estimates.length === 0 ? (
            <Typography color="text.secondary">No estimates yet.</Typography>
          ) : (
            estimates.map((e) => (
              <Card key={e.id} variant="outlined">
                <CardContent sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography sx={{ fontWeight: 800 }}>{e.label || `Estimate #${e.id}`}</Typography>
                    <Chip size="small" label={e.status} color={statusColor(e.status)} sx={{ mt: 0.5 }} />
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontWeight: 700 }}>{money(e.total, e.currency)}</Typography>
                    <Button
                      component={RouterLink}
                      to={`/public/client/${token}/estimate/${e.id}`}
                      size="small"
                      variant={e.can_respond ? 'contained' : 'outlined'}
                      sx={{ mt: 1, textTransform: 'none' }}
                    >
                      {e.can_respond ? 'Review & respond' : 'View estimate'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      ) : null}

      {tab === 2 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {invoices.length === 0 ? (
            <Typography color="text.secondary">No invoices yet.</Typography>
          ) : (
            invoices.map((inv) => (
              <Card key={inv.id} variant="outlined">
                <CardContent sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography sx={{ fontWeight: 800 }}>
                      {inv.invoice_number || `Invoice #${inv.id}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {inv.invoice_date || '—'}
                      {inv.due_date ? ` · due ${inv.due_date}` : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Chip
                      size="small"
                      label={inv.paid ? 'Paid' : 'Unpaid'}
                      color={inv.paid ? 'success' : 'warning'}
                      sx={{ mb: 0.5 }}
                    />
                    <Typography sx={{ fontWeight: 700 }}>{money(inv.total, 'GBP')}</Typography>
                    {!inv.paid ? (
                      <Button
                        component={RouterLink}
                        to={`/public/client/${token}/invoice/${inv.id}`}
                        size="small"
                        variant="contained"
                        sx={{ mt: 1, textTransform: 'none' }}
                      >
                        Pay invoice
                      </Button>
                    ) : null}
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      ) : null}

      <Divider sx={{ my: 3 }} />
      <Typography variant="caption" color="text.secondary">
        This link is personal. Do not share it. Contact us if you need a new link.
      </Typography>
    </Box>
    </PublicBrandShell>
  );
}
